/**
 * MarketplacePage - Workflow marketplace UI
 * ZAPHUB 2.0 - Buy/Sell workflows, browse, search, reviews
 */

import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { 
  Search, 
  Star, 
  ShoppingCart, 
  TrendingUp, 
  Filter,
  DollarSign,
  Download,
  Eye,
  Heart,
  Award,
  Sparkles,
} from 'lucide-react';
import type { MarketplaceWorkflow } from '@shared/types';
import { toast } from 'sonner';

export const MarketplacePage: React.FC = () => {
  const [listings, setListings] = useState<MarketplaceWorkflow[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState<MarketplaceWorkflow | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('popular');
  const [category, setCategory] = useState('all');
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchListings();
  }, [searchQuery, sortBy, category]);

  const fetchListings = async () => {
    setLoading(true);
    try {
      // Mock data - in production, call API
      const mockListings: MarketplaceWorkflow[] = [
        {
          id: '1',
          workflowId: 'wf1',
          name: 'Email Campaign Automator Pro',
          description: 'Automated email campaigns with AI personalization',
          longDescription: 'Complete email automation solution with AI-powered personalization, A/B testing, and advanced analytics.',
          category: 'Marketing',
          tags: ['email', 'marketing', 'ai', 'automation'],
          price: 99,
          currency: 'USD',
          sellerId: 'seller1',
          sellerName: 'John Doe',
          rating: 4.8,
          reviewCount: 127,
          purchaseCount: 543,
          forkCount: 89,
          screenshots: [],
          compatibility: ['2.0.0'],
          lastUpdate: new Date(),
          createdAt: new Date(),
          featured: true,
          verified: true,
        },
        {
          id: '2',
          workflowId: 'wf2',
          name: 'Lead Scoring & Qualification Bot',
          description: 'AI-powered lead qualification and scoring',
          category: 'Sales',
          tags: ['sales', 'crm', 'ai', 'leads'],
          price: 149,
          currency: 'USD',
          sellerId: 'seller2',
          sellerName: 'Jane Smith',
          rating: 4.9,
          reviewCount: 89,
          purchaseCount: 321,
          forkCount: 45,
          screenshots: [],
          compatibility: ['2.0.0'],
          lastUpdate: new Date(),
          createdAt: new Date(),
          featured: true,
          verified: true,
        },
        {
          id: '3',
          workflowId: 'wf3',
          name: 'Invoice Generator & Tracker',
          description: 'Automated invoicing with payment tracking',
          category: 'Finance',
          tags: ['finance', 'invoicing', 'accounting'],
          price: 79,
          currency: 'USD',
          sellerId: 'seller3',
          sellerName: 'Mike Johnson',
          rating: 4.7,
          reviewCount: 56,
          purchaseCount: 234,
          forkCount: 34,
          screenshots: [],
          compatibility: ['2.0.0'],
          lastUpdate: new Date(),
          createdAt: new Date(),
          featured: false,
          verified: true,
        },
        {
          id: '4',
          workflowId: 'wf4',
          name: 'Customer Support Ticket Router',
          description: 'Smart ticket routing with AI categorization',
          category: 'Support',
          tags: ['support', 'helpdesk', 'ai', 'tickets'],
          price: 129,
          currency: 'USD',
          sellerId: 'seller1',
          sellerName: 'John Doe',
          rating: 4.6,
          reviewCount: 72,
          purchaseCount: 198,
          forkCount: 28,
          screenshots: [],
          compatibility: ['2.0.0'],
          lastUpdate: new Date(),
          createdAt: new Date(),
          featured: false,
          verified: false,
        },
      ];

      setListings(mockListings);
    } catch (error) {
      console.error('Failed to fetch marketplace listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (workflow: MarketplaceWorkflow) => {
    setSelectedWorkflow(workflow);
    setShowPurchaseDialog(true);
  };

  const confirmPurchase = async () => {
    if (!selectedWorkflow) return;

    try {
      // Call API to purchase workflow
      console.log('Purchasing workflow:', selectedWorkflow.id);
      setShowPurchaseDialog(false);
      toast.success('Workflow purchased successfully!');
    } catch (error) {
      console.error('Purchase failed:', error);
      toast.error('Purchase failed. Please try again.');
    }
  };

  const categories = ['all', 'Marketing', 'Sales', 'Finance', 'Support', 'Productivity', 'HR'];

  return (
    <div className="h-full overflow-auto bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl mb-2">Workflow Marketplace</h1>
        <p className="text-gray-600">
          Discover and purchase professional workflows created by the ZAPHUB community
        </p>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 flex flex-wrap gap-4">
        <div className="flex-1 min-w-[300px] relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <Input
            placeholder="Search workflows..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-[180px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {categories.map((cat) => (
              <SelectItem key={cat} value={cat}>
                {cat === 'all' ? 'All Categories' : cat}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="popular">Most Popular</SelectItem>
            <SelectItem value="recent">Recently Added</SelectItem>
            <SelectItem value="rating">Highest Rated</SelectItem>
            <SelectItem value="price_low">Price: Low to High</SelectItem>
            <SelectItem value="price_high">Price: High to Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="browse" className="w-full">
        <TabsList>
          <TabsTrigger value="browse">Browse</TabsTrigger>
          <TabsTrigger value="featured">Featured</TabsTrigger>
          <TabsTrigger value="new">New Arrivals</TabsTrigger>
          <TabsTrigger value="top">Top Rated</TabsTrigger>
        </TabsList>

        <TabsContent value="browse" className="mt-6">
          {loading ? (
            <div className="text-center py-12">Loading workflows...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.map((workflow) => (
                <Card key={workflow.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="p-6">
                    {/* Header with badges */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg mb-1">{workflow.name}</h3>
                        <p className="text-sm text-gray-600">{workflow.category}</p>
                      </div>
                      {workflow.featured && (
                        <Badge variant="default" className="bg-yellow-500">
                          <Sparkles className="h-3 w-3 mr-1" />
                          Featured
                        </Badge>
                      )}
                    </div>

                    {/* Description */}
                    <p className="text-sm text-gray-700 mb-4 line-clamp-2">
                      {workflow.description}
                    </p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {workflow.tags.slice(0, 3).map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-2 mb-4 text-sm">
                      <div className="flex items-center gap-1">
                        <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                        <span>{workflow.rating}</span>
                        <span className="text-gray-500">({workflow.reviewCount})</span>
                      </div>
                      <div className="flex items-center gap-1 text-gray-600">
                        <Download className="h-4 w-4" />
                        <span>{workflow.purchaseCount}</span>
                      </div>
                      <div className="flex items-center gap-1 text-gray-600">
                        <Eye className="h-4 w-4" />
                        <span>{workflow.forkCount}</span>
                      </div>
                    </div>

                    {/* Seller */}
                    <div className="flex items-center gap-2 mb-4 pb-4 border-b">
                      <div className="h-8 w-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-sm">
                        {workflow.sellerName[0]}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm">{workflow.sellerName}</p>
                      </div>
                      {workflow.verified && (
                        <Award className="h-4 w-4 text-blue-500" />
                      )}
                    </div>

                    {/* Price and Action */}
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-2xl">${workflow.price}</span>
                        <span className="text-gray-500 text-sm ml-1">USD</span>
                      </div>
                      <Button onClick={() => handlePurchase(workflow)}>
                        <ShoppingCart className="h-4 w-4 mr-2" />
                        Purchase
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="featured">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.filter((w) => w.featured).map((workflow) => (
              <Card key={workflow.id} className="p-6">
                <h3 className="mb-2">{workflow.name}</h3>
                <p className="text-sm text-gray-600 mb-4">{workflow.description}</p>
                <Button onClick={() => handlePurchase(workflow)} className="w-full">
                  Purchase - ${workflow.price}
                </Button>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="new">
          <div className="text-center py-12 text-gray-500">
            New arrivals will appear here
          </div>
        </TabsContent>

        <TabsContent value="top">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings
              .sort((a, b) => b.rating - a.rating)
              .slice(0, 6)
              .map((workflow) => (
                <Card key={workflow.id} className="p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                    <span className="text-lg">{workflow.rating}</span>
                  </div>
                  <h3 className="mb-2">{workflow.name}</h3>
                  <p className="text-sm text-gray-600 mb-4">{workflow.description}</p>
                  <Button onClick={() => handlePurchase(workflow)} className="w-full">
                    Purchase - ${workflow.price}
                  </Button>
                </Card>
              ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Purchase Dialog */}
      <Dialog open={showPurchaseDialog} onOpenChange={setShowPurchaseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Purchase Workflow</DialogTitle>
            <DialogDescription>
              You are about to purchase "{selectedWorkflow?.name}"
            </DialogDescription>
          </DialogHeader>

          {selectedWorkflow && (
            <div className="py-4 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Price:</span>
                <span className="text-2xl">${selectedWorkflow.price} USD</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Seller earns:</span>
                <span className="text-lg">${(selectedWorkflow.price * 0.7).toFixed(2)} USD</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Platform fee (30%):</span>
                <span className="text-sm">${(selectedWorkflow.price * 0.3).toFixed(2)} USD</span>
              </div>
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-600">
                  After purchase, the workflow will be added to your account and you can start using it immediately.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPurchaseDialog(false)}>
              Cancel
            </Button>
            <Button onClick={confirmPurchase}>
              <ShoppingCart className="h-4 w-4 mr-2" />
              Confirm Purchase
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};